import { useInjectReducer } from "@app/store";
import Checkbox from "@components/Checkbox";
import DeleteDialog from "@components/DeleteDialog";
import { FilterInput, FilterSelect } from "@components/FilterInputs";
import PageContent from "@components/PageContent";
import PageFilter from "@components/PageFilter";
import PageHeader from "@components/PageHeader";
import Table from "@components/Table";
import {
  useActiveAllLocationRequestMutation,
  useDeleteMultipleLocationRequestMutation,
  useDeleteOnePickupLocationMutation,
  useLoadAllPickupLocationMutation,
} from "@features/admin/shipping/pickupLocation/pickupLocationApiSlice";
import {
  makeSelectQuery,
  makeSelectAll,
  makeSelectChosenLocation,
} from "@features/admin/shipping/pickupLocation/pickupLocationSelector";
import pickupLocationSlice, {
  addChosenLocation,
  clearChosenLocation,
  clearOne,
  setQueryValue,
} from "@features/admin/shipping/pickupLocation/pickupLocationSlice";
import { useLoadSellerQuery } from "@features/admin/productsManage/productsApiSlice";
import { enqueueSnackbar, makeSelectAccess } from "@features/auth/authSlice";
import { checkAccess } from "@utils/helpers";
import { queryHelper } from "@utils/queryHelper";
import Button from "@components/Button";
import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { FaPencilAlt, FaPlus, FaTrash } from "react-icons/fa";
import { RiSearch2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MultiCheck from "@components/MultiCheck";

const PickupLocation = () => {
  useInjectReducer("pickupLocation", pickupLocationSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const query = useSelector(makeSelectQuery);
  const access = useSelector(makeSelectAccess);
  const all = useSelector(makeSelectAll);
  const chosen_location = useSelector(makeSelectChosenLocation);

  const [
    allPickupLocation,
    { data: allPickupLocationData, isLoading: loading, isSuccess: success },
  ] = useLoadAllPickupLocationMutation();

  const [deletePickupLocation] = useDeleteOnePickupLocationMutation();
  const [deleteMultipleLocation] = useDeleteMultipleLocationRequestMutation();
  const [activeAllLocation] = useActiveAllLocationRequestMutation();

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [multipleOpen, setMultipleOpen] = useState(false);

  const { data: sellerInfo, isLoading: sellerLoading } = useLoadSellerQuery();
  const sellerType = sellerInfo?.data ?? [];

  const [queryObj, setQueryObj] = useState({});
  const handleChangeQueryDropdown = (name) => (e) => {
    setQueryObj({ ...queryObj, [name]: e.value });
  };
  const { sellerTypeOptions } = useMemo(() => {
    let sellerTypeOptions = [];
    if (!sellerLoading && sellerType) {
      sellerTypeOptions = sellerType?.map((each) => ({
        label: each?.company?.company_name,
        value: each._id,
      }));
    }
    return { sellerTypeOptions };
  }, [sellerLoading]);

  useEffect(() => {
    allPickupLocation(queryHelper(query));
  }, []);

  const handleOpen = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAdd = () => {
    dispatch(clearOne());
    navigate("/admin/pickup/add");
  };

  const handleEdit = (id) => {
    navigate(`/admin/pickup/edit/${id}`);
  };

  // const handleSearch = () => {
  //   allPickupLocation(queryHelper(query))
  // }
  const handleSearch = () => {
    dispatch(setQueryValue({ ...query, ...queryObj }));
    allPickupLocation(queryHelper({ ...query, ...queryObj }));
  };

  const handleQueryChange = (name) => (event) => {
    dispatch(setQueryValue({ key: name, value: event.target.value }));
  };

  const handleMultiCheckedChange = () => {
    all.data.map((each) => {
      dispatch(addChosenLocation(each._id));
    });
  };

  const handleClear = () => {
    dispatch(clearChosenLocation());
  };

  const handleOpenForMultiple = (id) => {
    setMultipleOpen(true);
    setDeleteId(id);
  };

  const handleCloseForMultiple = () => {
    setMultipleOpen(false);
  };

  const handleDelete = () => {
    deletePickupLocation(deleteId)
      .unwrap()
      .then((res) => {
        dispatch(
          enqueueSnackbar({
            message: res?.msg ?? "Pickup Location Deleted.",
            options: {
              variant: "success",
            },
          })
        );
        allPickupLocation(queryHelper(query));
        setOpen(false);
      })
      .catch((err) => {
        enqueueSnackbar({
          message: err?.data?.msg ?? "Pickup Location Delete Failed",
          options: {
            variant: "warning",
          },
        });
      });
  };

  const handleDeleteForMultiple = () => {
    deleteMultipleLocation(deleteId)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message:
                res?.msg ?? "Multiple Pickup Location Deleted Successfully.",
              options: {
                variant: "success",
              },
            })
          );
          allPickupLocation(queryHelper(query));
          setOpen(false);
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Something Went Wrong.",
            options: {
              variant: "warning",
            },
          })
        );
      });
    setMultipleOpen(false);
  };

  const handleActiveAll = () => {
    activeAllLocation(chosen_location)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Status Changed Successfully.",
              options: {
                variant: "success",
              },
            })
          );
          allPickupLocation(queryHelper(query));
          setOpen(false);
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Status Change Failed.",
            options: {
              variant: "warning",
            },
          })
        );
      });
  };

  const tableData = success
    ? allPickupLocationData?.data?.map(
        ({ title, location: location2, is_active, _id }) => [
          <div className='checkbox' style={{ marginRight: 0 }}>
            <Checkbox
              name={_id}
              checked={chosen_location.includes(_id)}
              onChange={() => dispatch(addChosenLocation(_id))}
            />
          </div>,
          title ? title : "-",
          location2 ? location2 : "-",
          is_active ? "Active" : "Not Active",
          <>
            <div className='flex justify-center'>
              {checkAccess("edit/:id", access) && (
                <button
                  aria-label='Edit'
                  className='px-1 text-center leading-none'
                  onClick={() => handleEdit(_id)}
                >
                  <FaPencilAlt className='tblpencil text-xs text-blue-400 hover:text-blue-500' />
                </button>
              )}
              {checkAccess("delete", access) && (
                <button
                  className='ml-2 px-1 text-center leading-none'
                  onClick={() => handleOpen(_id)}
                >
                  <FaTrash className='tbltrash text-xs text-red-400 hover:text-red-500' />
                </button>
              )}
            </div>
          </>,
        ]
      )
    : [];

  const handlePagination = ({ page, size }) => {
    allPickupLocation(queryHelper(query));
    dispatch(setQueryValue({ key: "page", value: page }));
    allPickupLocation(queryHelper({ ...query, page, size }));
  };

  const tablePagination = {
    page: allPickupLocationData?.page ?? 1,
    size: allPickupLocationData?.size ?? 10,
    totalData: allPickupLocationData?.totalData ?? 0,
  };

  return (
    <>
      <DeleteDialog
        open={open}
        doClose={handleClose}
        doDelete={() => handleDelete(deleteId)}
      />
      <DeleteDialog
        open={multipleOpen}
        doClose={handleCloseForMultiple}
        doDelete={() => handleDeleteForMultiple(chosen_location)}
      />
      <Helmet>
        <title> Pickup Location Manage </title>
      </Helmet>
      <div className='flex justify-between mt-3 mb-3' id='page-header'>
        <PageHeader title='Pickup Location Manage' />
        {checkAccess("add", access) && (
          <div className='flex items-center'>
            <button className='btn btn-admin bg-success' onClick={handleAdd}>
              <div className='flex items-center'>
                <FaPlus />
                <span className='pl-2'> Add New </span>
              </div>
            </button>
          </div>
        )}
      </div>
      <PageFilter>
        <div className='flex justify-between items-center'>
          <div className='flex gap-2'>
            <FilterInput
              label='Search Location By Title'
              fieldsetId='constents-name'
              value={query?.find_title ?? ""}
              onChange={handleQueryChange("find_title")}
            />
            <FilterSelect
              label='Search By Seller'
              fieldsetId='prescription_required'
              options={[{ label: "All", value: "" }, ...sellerTypeOptions]}
              onChange={handleChangeQueryDropdown("find_by_seller")}
            />
            <Button
              isFilter
              leftSection={<RiSearch2Line />}
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
          <div>
            {chosen_location.length > 0 ? (
              <div className='flex items-center'>
                <button
                  className='btn bg-danger mr-2'
                  onClick={() => handleOpenForMultiple(chosen_location)}
                >
                  <div className='flex items-center'>
                    <FaTrash />
                    <span className='pl-2'> Delete </span>
                  </div>
                </button>
                <button
                  className='btn bg-success'
                  onClick={() => handleActiveAll(chosen_location)}
                >
                  <div className='flex items-center'>
                    <span className='pl-2'> Change Status </span>
                  </div>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </PageFilter>
      <PageContent loading={loading}>
        <Table
          tableHead={[
            <MultiCheck
              all={all}
              chosen_items={chosen_location}
              selectChange={handleMultiCheckedChange}
              clearSelectedItems={handleClear}
            />,
            "Title",
            "Location",
            "Status",
            <span className='flex justify-center'> Action </span>,
          ]}
          tableData={tableData}
          pagination={tablePagination}
          handlePagination={handlePagination}
          emptyDataMsg={`No Location Found`}
          loading={loading}
          ispublic
        />
      </PageContent>
    </>
  );
};

export default PickupLocation;
